import {
  Component,
  OnInit,
  ViewChild,
  Output,
  Input,
  EventEmitter,
  HostListener,
  AfterViewInit,
  ElementRef,
  OnDestroy,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { AlertTriangle } from 'lucide-angular';
import { TableLazyLoadEvent } from 'primeng/table';
import { WorkOrderService } from '../../../services/work-order.service';
import { SimilarWorkOrderService } from '../../../services/similar-work-order.service';
import { WorkOrderSuggestedCategoryService } from '../../../services/work-order-suggested-category.service';
import { Popover } from 'primeng/popover';
import { MessageService } from 'primeng/api';
import {
  defaultOptions,
  priorityFilterOptions,
  readyToScheduleFilterOptions,
  similarOrderFilterOptions,
  userStatusOptions,
  workOrderTypeFilterOptions,
} from '../../../shared/constants/sanitization-options.constans';
import { SelectItem } from 'primeng/api';
import { SimilarWorkOrder, WorkOrder } from '../../../models/work-order.model';
import { cloneDeep } from 'lodash';
import { PagedRequest } from '../../../models/requests/paged-request';
import { getScoreColorClass } from '../../../../utils/score-color.utils';
import {
  SanitizationFilterService,
  SHARED_FILTER_FIELDS,
  WORK_ORDER_FILTER_FIELDS,
} from '../../../services/sanitization-filter.service';
@Component({
  selector: 'app-work-order',
  templateUrl: './work-order.component.html',
  styleUrl: './work-order.component.scss',
  animations: [
    trigger('slidePanel', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scaleX(0)', transformOrigin: 'right' }),
        animate(
          '300ms ease-out',
          style({ opacity: 1, transform: 'scaleX(1)' }),
        ),
      ]),
      transition(':leave', [
        style({ transformOrigin: 'right' }),
        animate(
          '300ms ease-out',
          style({ opacity: 0, transform: 'scaleX(0)' }),
        ),
      ]),
    ]),
  ],
  standalone: false,
})
export class WorkOrderComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly AlertTriangle = AlertTriangle;

  @ViewChild('similarPopover') similarPopover!: Popover;
  @ViewChild('priorityPopover') priorityPopover!: Popover;
  @ViewChild('prioritySettingsPopover') prioritySettingsPopover!: Popover;
  @ViewChild('workOrderTable', { static: false }) workOrderTable: any;
  @ViewChild('tableContainer', { static: false }) tableContainer: any;
  @Input() plantOptions: SelectItem[] = defaultOptions;
  @Input() mainWorkCenterOptions: SelectItem[] = defaultOptions;
  @Output() totalRecordsChange = new EventEmitter<number>();
  private destroy$ = new Subject<void>();
  private tableInitialized = false;
  createdOnDate: Date | Date[] | null = null;
  originalDueDate: Date | Date[] | null = null;
  workOrders: WorkOrder[] = [];
  selectedSimilarOrder: SimilarWorkOrder | null = null;
  currentOrderSimilars: SimilarWorkOrder[] = [];
  totalRecords = 0;
  loading = true;
  // selectedOrder: WorkOrder | null = null;
  selectedOrderForPriority: WorkOrder | null = null;
  selectedPriority: string | null = null;
  showHideReasonDialog = false;
  private priorityHoverTimeout: any = null;
  panelVisible = false;
  private ro?: ResizeObserver;
  private lastMouseEvent?: MouseEvent;
  private lastIconEl?: HTMLElement;
  priorityPanelVisible = false;
  private priorityRo?: ResizeObserver;
  private priorityHideTimeout: any = null;
  private isPriorityHovered = false;
  private lastSimilarMouseEvent?: MouseEvent;
  private lastSimilarEl?: HTMLElement;
  similarPanelVisible = false;
  private similarRo?: ResizeObserver;
  private similarHideTimeout: any = null;
  private isSimilarHovered = false;
  private similarHoverTimeout: any = null;
  hideReasonText = '';
  currentOrderToHide: WorkOrder | null = null;
  selectedWorkOrderTypeFilter: string | null = null;
  selectedSimilarOrderFilter: string | null = null;
  selectedPlant: string | null = null;
  selectedMainWorkCenters: string[] | null = null;
  selectedUserStatus: string | null = null;
  showSuggestedPriorities = false;
  similarOrderFilterOptions: SelectItem[] = similarOrderFilterOptions;
  priorityFilterOptions: SelectItem[] = priorityFilterOptions;
  workOrderTypeFilterOptions: SelectItem[] = workOrderTypeFilterOptions;
  userStatusOptions: SelectItem[] = userStatusOptions;
  exportLoading = false;
  exportDuplicatesLoading = false;
  rightPanelOpen = false;
  selectedOrder: WorkOrder | null = null;
  initialFilters: any = {};
  hasActiveFilters = false;
  private currentFilters: Record<string, any> = {};

  get filterFields() {
    return WORK_ORDER_FILTER_FIELDS;
  }

  private readonly INITIAL_TABLE_STATE = {
    first: 0,
    rows: 25,
    sortField: null,
    sortOrder: 0,
    filters: {
      [WORK_ORDER_FILTER_FIELDS.WORK_ORDER_ID]: {
        value: null,
        matchMode: 'contains',
      },
      [WORK_ORDER_FILTER_FIELDS.PLANT]: { value: null, matchMode: 'contains' },
      [WORK_ORDER_FILTER_FIELDS.MAIN_WORK_CENTER]: {
        value: null,
        matchMode: 'in',
      },
      [WORK_ORDER_FILTER_FIELDS.FUNCTIONAL_LOCATION]: {
        value: null,
        matchMode: 'contains',
      },
      [WORK_ORDER_FILTER_FIELDS.FUNCTIONAL_LOCATION_ID]: {
        value: null,
        matchMode: 'contains',
      },
      [WORK_ORDER_FILTER_FIELDS.CREATED_ON]: {
        value: null,
        matchMode: 'equals',
      },
      [WORK_ORDER_FILTER_FIELDS.ORIGINAL_DUE_DATE]: {
        value: null,
        matchMode: 'equals',
      },
      [WORK_ORDER_FILTER_FIELDS.PRIORITY]: {
        value: null,
        matchMode: 'contains',
      },
      [WORK_ORDER_FILTER_FIELDS.DESCRIPTION]: {
        value: null,
        matchMode: 'contains',
      },
      [WORK_ORDER_FILTER_FIELDS.DUPLICATES]: {
        value: null,
        matchMode: 'equals',
      },
      [WORK_ORDER_FILTER_FIELDS.WORK_ORDER_TYPE]: {
        value: null,
        matchMode: 'contains',
      },
      [WORK_ORDER_FILTER_FIELDS.USER_STATUS]: {
        value: null,
        matchMode: 'contains',
      },
    },
  };

  constructor(
    private workOrderService: WorkOrderService,
    private similarWorkOrderService: SimilarWorkOrderService,
    private workOrderSuggestedCategoryService: WorkOrderSuggestedCategoryService,
    private messageService: MessageService,
    private filterService: SanitizationFilterService,
  ) {}

  ngOnInit() {
    this.restoreFilters();
    this.initialFilters = this.buildInitialFilters();
    this.subscribeToPlant();
    this.subscribeToMainWorkCenter();
    this.filterService.clearAll$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.resetTable());
  }

  private subscribeToPlant(): void {
    this.filterService.plant$.pipe(takeUntil(this.destroy$)).subscribe((v) => {
      this.selectedPlant = v;
      if (this.tableInitialized && !this.filterService.isClearing)
        this.applySharedFilterToTable(SHARED_FILTER_FIELDS.PLANT, v);
    });
  }

  private subscribeToMainWorkCenter(): void {
    this.filterService.mainWorkCenters$
      .pipe(takeUntil(this.destroy$))
      .subscribe((v) => {
        this.selectedMainWorkCenters = v;
        if (this.tableInitialized && !this.filterService.isClearing)
          this.applySharedFilterToTable(
            SHARED_FILTER_FIELDS.MAIN_WORK_CENTER,
            v,
          );
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applySharedFilterToTable(
    field: string,
    value: string | string[] | null,
  ): void {
    if (!this.workOrderTable) return;
    const matchMode = Array.isArray(value) ? 'in' : 'contains';
    this.workOrderTable.filters = {
      ...this.workOrderTable.filters,
      [field]: { value, matchMode },
    };
    this.workOrderTable._filter();
  }

  private restoreFilters(): void {
    const shared = this.filterService.getShared();
    const own = this.filterService.getWorkOrder();
    this.selectedPlant = shared.plant;
    this.selectedMainWorkCenters = shared.mainWorkCenters;
    this.createdOnDate = own.createdOn
      ? own.createdOn.split(',').map((d) => new Date(d))
      : null;
    this.originalDueDate = own.originalDueDate
      ? own.originalDueDate.split(',').map((d) => new Date(d))
      : null;
    this.selectedPriority = own.priority;
    this.selectedSimilarOrderFilter = own.duplicates;
    this.selectedWorkOrderTypeFilter = own.workOrderType;
    this.selectedUserStatus = own.userStatus;
  }

  ngAfterViewInit() {
    this.tableInitialized = true;
    const hasAny =
      Object.values(this.filterService.getShared()).some((v) => v !== null) ||
      Object.values(this.filterService.getWorkOrder()).some((v) => v !== null);
    if (hasAny) setTimeout(() => this.workOrderTable?._filter());
  }

  private buildInitialFilters(): any {
    const shared = this.filterService.getShared();
    const own = this.filterService.getWorkOrder();
    const filters: any = { ...cloneDeep(this.INITIAL_TABLE_STATE.filters) };
    if (shared.plant)
      filters[WORK_ORDER_FILTER_FIELDS.PLANT] = {
        value: shared.plant,
        matchMode: 'contains',
      };
    if (shared.mainWorkCenters?.length)
      filters[WORK_ORDER_FILTER_FIELDS.MAIN_WORK_CENTER] = {
        value: shared.mainWorkCenters,
        matchMode: 'in',
      };
    if (shared.functionalLocation)
      filters[WORK_ORDER_FILTER_FIELDS.FUNCTIONAL_LOCATION] = {
        value: shared.functionalLocation,
        matchMode: 'contains',
      };
    if (own.functionalLocationId)
      filters[WORK_ORDER_FILTER_FIELDS.FUNCTIONAL_LOCATION_ID] = {
        value: own.functionalLocationId,
        matchMode: 'contains',
      };
    if (own.workOrderId)
      filters[WORK_ORDER_FILTER_FIELDS.WORK_ORDER_ID] = {
        value: own.workOrderId,
        matchMode: 'contains',
      };
    if (own.priority)
      filters[WORK_ORDER_FILTER_FIELDS.PRIORITY] = {
        value: own.priority,
        matchMode: 'contains',
      };
    if (own.description)
      filters[WORK_ORDER_FILTER_FIELDS.DESCRIPTION] = {
        value: own.description,
        matchMode: 'contains',
      };
    if (own.duplicates)
      filters[WORK_ORDER_FILTER_FIELDS.DUPLICATES] = {
        value: own.duplicates,
        matchMode: 'equals',
      };
    if (own.workOrderType)
      filters[WORK_ORDER_FILTER_FIELDS.WORK_ORDER_TYPE] = {
        value: own.workOrderType,
        matchMode: 'contains',
      };
    if (own.userStatus)
      filters[WORK_ORDER_FILTER_FIELDS.USER_STATUS] = {
        value: own.userStatus,
        matchMode: 'contains',
      };
    if (own.createdOn)
      filters[WORK_ORDER_FILTER_FIELDS.CREATED_ON] = {
        value: own.createdOn.split(',').map((d: string) => new Date(d)),
        matchMode: 'equals',
      };
    if (own.originalDueDate)
      filters[WORK_ORDER_FILTER_FIELDS.ORIGINAL_DUE_DATE] = {
        value: own.originalDueDate.split(',').map((d: string) => new Date(d)),
        matchMode: 'equals',
      };
    return filters;
  }

  onPlantFilterChange(value: string | null): void {
    this.filterService.setPlant(value);
  }

  onMainWorkCenterFilterChange(
    value: string[] | null,
    filterCallback: (v: any) => void,
  ): void {
    const normalizedValue = value && value.length > 0 ? value : null;
    this.filterService.setMainWorkCenters(normalizedValue);
    filterCallback(normalizedValue);
  }

  onFunctionalLocationFilterChange(
    value: string | null,
    filterCallback: (v: any) => void,
  ): void {
    this.filterService.setFunctionalLocation(value);
    filterCallback(value);
  }

  onSimilarOrderFilterChange(
    value: string | null,
    filterCallback: (v: any) => void,
  ): void {
    this.filterService.setWorkOrder({ duplicates: value || null });
    filterCallback(value || null);
  }

  getPriorityClass(priority: string | undefined): string {
    switch (priority) {
      case 'A':
        return 'bg-red-100 text-red-500';
      case 'B':
        return 'bg-orange-100 text-orange-500';
      case 'C':
        return 'bg-emerald-100 text-emerald-500';
      default:
        return 'bg-gray-100 text-gray-500';
    }
  }

  getSimilarityScoreClass(score: number): string {
    return getScoreColorClass(score);
  }

  getUserStatusClass(status: string | undefined): string {
    switch (status) {
      case 'OPEN':
        return 'bg-green-400/85';
      case 'DFSC':
        return 'bg-yellow-400/85';
      case 'RPWO':
        return 'bg-orange-400/85';
      case 'FNSC':
        return 'bg-blue-500/85';
      default:
        return 'bg-gray-400';
    }
  }

  getPriorityBadgeClass(order: WorkOrder, priority: string): string {
    if (
      order?.SAP_Priority === priority &&
      order?.SuggestedPriority === priority
    ) {
      return 'split-priority text-white';
    }
    if (order?.SAP_Priority === priority) {
      return 'bg-blue-500/70 text-white';
    }
    if (
      order?.SuggestedPriority === priority &&
      order?.SAP_Priority !== priority
    ) {
      return 'bg-red-400/90 text-white shadow-red-200';
    }
    return 'bg-gray-200 text-gray-700';
  }

  getParentWorkOrder(sourceWorkOrderId: string): WorkOrder | undefined {
    return this.workOrders.find(
      (wo) => wo.SAP_WorkOrderId === sourceWorkOrderId,
    );
  }

  onSuggestedPriorityHover(order: WorkOrder, event: MouseEvent) {
    event.stopPropagation();
    this.isPriorityHovered = true;

    if (this.priorityHideTimeout) {
      clearTimeout(this.priorityHideTimeout);
      this.priorityHideTimeout = null;
    }

    if (this.priorityHoverTimeout) {
      clearTimeout(this.priorityHoverTimeout);
    }

    const isDifferent =
      this.selectedOrderForPriority?.SAP_WorkOrderId !== order.SAP_WorkOrderId;
    this.selectedOrderForPriority = order;
    this.lastMouseEvent = event;
    this.lastIconEl = event.target as HTMLElement;

    if (this.priorityPopover?.overlayVisible && isDifferent) {
      this.priorityRo?.disconnect();
      const panelEl = this.priorityPopover?.container as HTMLElement;
      if (panelEl) {
        panelEl.style.display = 'none';
      }
      this.priorityPopover.hide();
      this.priorityHoverTimeout = setTimeout(() => {
        if (
          this.isPriorityHovered &&
          this.selectedOrderForPriority?.SAP_WorkOrderId ===
            order.SAP_WorkOrderId
        ) {
          this.priorityPopover?.show(event);
        }
      }, 150);
    } else if (!this.priorityPopover?.overlayVisible) {
      this.priorityHoverTimeout = setTimeout(() => {
        if (
          this.isPriorityHovered &&
          this.selectedOrderForPriority?.SAP_WorkOrderId ===
            order.SAP_WorkOrderId
        ) {
          this.priorityPopover?.show(event);
        }
      }, 150);
    }
  }

  onSuggestedPriorityLeave() {
    this.isPriorityHovered = false;
    if (this.priorityHoverTimeout) {
      clearTimeout(this.priorityHoverTimeout);
      this.priorityHoverTimeout = null;
    }
    this.priorityHideTimeout = setTimeout(() => {
      this.priorityPopover?.hide();
    }, 100);
  }

  onPriorityPopoverShow() {
    this.priorityPanelVisible = true;
    const panelEl = this.priorityPopover?.container as HTMLElement;
    if (panelEl) {
      panelEl.style.display = 'block';
    }
  }

  onPriorityPopoverHide() {
    this.priorityPanelVisible = false;
    this.priorityRo?.disconnect();
    this.priorityRo = undefined;
  }

  private positionPopover() {
    const panelEl = document.querySelector<HTMLElement>('.p-popover');
    if (!panelEl || !this.lastIconEl) return;

    const iconRect = this.lastIconEl.getBoundingClientRect();
    const panelH = panelEl.offsetHeight;
    const panelW = panelEl.offsetWidth;
    const gap = 8;

    panelEl.style.position = 'fixed';

    const spaceBelow = window.innerHeight - iconRect.bottom;
    const spaceAbove = iconRect.top;
    const shouldFlip = panelH + gap > spaceBelow && spaceAbove > spaceBelow;

    // let top = shouldFlip ? iconRect.top - panelH - gap : iconRect.bottom + gap;
    // top = Math.min(Math.max(15, top), window.innerHeight - panelH - 15);
    // panelEl.style.top = `${top}px`;
  }

  onSimilarOrderHover(similarOrder: SimilarWorkOrder, event: MouseEvent) {
    event.stopPropagation();
    this.isSimilarHovered = true;

    if (this.similarHideTimeout) {
      clearTimeout(this.similarHideTimeout);
      this.similarHideTimeout = null;
    }

    if (this.similarHoverTimeout) {
      clearTimeout(this.similarHoverTimeout);
    }

    const isDifferent =
      this.selectedSimilarOrder?.Similar_SAP_WorkOrderId !==
      similarOrder.Similar_SAP_WorkOrderId;
    this.selectedSimilarOrder = similarOrder;
    this.lastSimilarMouseEvent = event;
    this.lastSimilarEl = event.target as HTMLElement;

    if (this.similarPopover?.overlayVisible && isDifferent) {
      this.similarRo?.disconnect();
      const panelEl = this.similarPopover?.container as HTMLElement;
      if (panelEl) {
        panelEl.style.display = 'none';
      }
      this.similarPopover.hide();
      this.similarHoverTimeout = setTimeout(() => {
        if (
          this.isSimilarHovered &&
          this.selectedSimilarOrder?.Similar_SAP_WorkOrderId ===
            similarOrder.Similar_SAP_WorkOrderId
        ) {
          this.similarPopover?.show(event);
        }
      }, 200);
    } else if (!this.similarPopover?.overlayVisible) {
      this.similarHoverTimeout = setTimeout(() => {
        if (
          this.isSimilarHovered &&
          this.selectedSimilarOrder?.Similar_SAP_WorkOrderId ===
            similarOrder.Similar_SAP_WorkOrderId
        ) {
          this.similarPopover?.show(event);
        }
      }, 200);
    }
  }

  onSimilarOrderLeave() {
    this.isSimilarHovered = false;
    if (this.similarHoverTimeout) {
      clearTimeout(this.similarHoverTimeout);
      this.similarHoverTimeout = null;
    }
    this.similarHideTimeout = setTimeout(() => {
      this.similarPopover?.hide();
    }, 100);
  }

  onSimilarPopoverShow() {
    this.similarPanelVisible = true;
    const panelEl = this.similarPopover?.container as HTMLElement;
    if (panelEl) {
      panelEl.style.display = 'block';
    }
  }

  onSimilarPopoverHide() {
    this.similarPanelVisible = false;
    this.similarRo?.disconnect();
    this.similarRo = undefined;
  }

  // onRowSelect(workOrder: WorkOrder) {
  //   this.selectedOrder = workOrder;
  // }

  private isInvalidDateRange(filter: any): boolean {
    if (
      filter &&
      !Array.isArray(filter) &&
      filter.value !== null &&
      filter.value !== undefined
    ) {
      const dateRange = Array.isArray(filter.value)
        ? filter.value
        : [filter.value];
      return !dateRange[0] || !dateRange[1];
    }
    return false;
  }

  private computeHasActiveFilters(filters: any): boolean {
    return Object.values(WORK_ORDER_FILTER_FIELDS).some(
      (field) => this.getFilterValue(filters, field) !== null,
    );
  }

  onClearFilters(): void {
    this.filterService.clearAll();
  }

  private resetTable(): void {
    this.selectedPlant = null;
    this.selectedMainWorkCenters = null;
    this.createdOnDate = null;
    this.originalDueDate = null;
    this.selectedPriority = null;
    this.selectedSimilarOrderFilter = null;
    this.selectedWorkOrderTypeFilter = null;
    this.selectedUserStatus = null;
    const clearedFilters = cloneDeep(this.INITIAL_TABLE_STATE.filters);
    this.initialFilters = clearedFilters;
    if (this.workOrderTable) {
      this.workOrderTable.filters = clearedFilters;
      this.workOrderTable.first = 0;
      this.workOrderTable.sortField = null;
      this.workOrderTable.sortOrder = 0;
      this.workOrderTable._filter();
    }
  }

  loadData(lazyLoadEvent: TableLazyLoadEvent) {
    const filters = cloneDeep(lazyLoadEvent.filters ?? {});
    this.hasActiveFilters = this.computeHasActiveFilters(filters);

    this.filterService.persistShared({
      plant: this.getFilterValue(filters, WORK_ORDER_FILTER_FIELDS.PLANT),
      mainWorkCenters: this.getFilterValue(
        filters,
        WORK_ORDER_FILTER_FIELDS.MAIN_WORK_CENTER,
      ),
      functionalLocation: this.getFilterValue(
        filters,
        WORK_ORDER_FILTER_FIELDS.FUNCTIONAL_LOCATION,
      ),
    });
    this.filterService.setWorkOrder({
      workOrderId: this.getFilterValue(
        filters,
        WORK_ORDER_FILTER_FIELDS.WORK_ORDER_ID,
      ),
      createdOn: this.serializeDateFilter(
        filters[WORK_ORDER_FILTER_FIELDS.CREATED_ON],
      ),
      originalDueDate: this.serializeDateFilter(
        filters[WORK_ORDER_FILTER_FIELDS.ORIGINAL_DUE_DATE],
      ),
      priority: this.getFilterValue(filters, WORK_ORDER_FILTER_FIELDS.PRIORITY),
      description: this.getFilterValue(
        filters,
        WORK_ORDER_FILTER_FIELDS.DESCRIPTION,
      ),
      duplicates: this.getFilterValue(
        filters,
        WORK_ORDER_FILTER_FIELDS.DUPLICATES,
      ),
      workOrderType: this.getFilterValue(
        filters,
        WORK_ORDER_FILTER_FIELDS.WORK_ORDER_TYPE,
      ),
      userStatus: this.getFilterValue(
        filters,
        WORK_ORDER_FILTER_FIELDS.USER_STATUS,
      ),
      functionalLocationId: this.getFilterValue(
        filters,
        WORK_ORDER_FILTER_FIELDS.FUNCTIONAL_LOCATION_ID,
      ),
    });

    if (this.isInvalidDateRange(filters[WORK_ORDER_FILTER_FIELDS.CREATED_ON])) {
      delete filters[WORK_ORDER_FILTER_FIELDS.CREATED_ON];
    }
    if (
      this.isInvalidDateRange(
        filters[WORK_ORDER_FILTER_FIELDS.ORIGINAL_DUE_DATE],
      )
    ) {
      delete filters[WORK_ORDER_FILTER_FIELDS.ORIGINAL_DUE_DATE];
    }
    this.currentFilters = filters;
    this.loading = true;
    const payload: PagedRequest = {
      First: lazyLoadEvent.first ?? 0,
      Rows: lazyLoadEvent.rows ?? 10,
      SortField: lazyLoadEvent.sortField ?? null,
      SortOrder: lazyLoadEvent.sortOrder ?? 0,
      Filters: filters,
    };

    this.workOrderService.getPaged(payload).subscribe({
      next: (response) => {
        this.workOrders = response.Items;
        this.totalRecords = response.TotalRecords;
        this.totalRecordsChange.emit(this.totalRecords);
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading work orders:', error);
        this.loading = false;
      },
    });
  }

  onHidePriorityDescription(order: WorkOrder) {
    this.currentOrderToHide = order;
    this.hideReasonText = '';
    this.showHideReasonDialog = true;
    this.priorityPopover.hide();
  }

  onTogglePriorityVisibility(order: WorkOrder, event: Event) {
    event.stopPropagation();
    this.currentOrderToHide = order;
    this.hideReasonText = '';
    this.showHideReasonDialog = true;
    this.priorityPopover.hide();
  }

  onPrioritySelect(order: WorkOrder, priority: string, event: Event) {
    event.stopPropagation();

    if (order.UserStatus !== 'OPEN') {
      this.messageService.add({
        key: 'globalToast',
        severity: 'warn',
        summary: 'Modification impossible',
        detail:
          'La priorité ne peut être modifiée que pour les ordres avec le statut OPEN',
        life: 3000,
      });
      return;
    }

    this.workOrderService
      .updateSelectedPriority(order.SAP_WorkOrderId, priority)
      .subscribe({
        next: (updatedWorkOrder: WorkOrder) => {
          const index = this.workOrders.findIndex(
            (wo) => wo.SAP_WorkOrderId === updatedWorkOrder.SAP_WorkOrderId,
          );
          if (index !== -1) {
            this.workOrders[index].SelectedPriority =
              updatedWorkOrder.SelectedPriority;
            this.workOrders[index].SelectedPriorityChangedDatetime =
              updatedWorkOrder.SelectedPriorityChangedDatetime;
            this.workOrders[index].SelectedPriorityChangedByUser =
              updatedWorkOrder.SelectedPriorityChangedByUser;
          }
          this.messageService.add({
            key: 'globalToast',
            severity: 'success',
            summary: 'Priorité sélectionnée',
            detail: 'La priorité a été mise à jour avec succès',
            life: 1000,
          });
        },
        error: (error) => {
          console.error('Error updating priority:', error);
        },
      });
  }

  onSimilarOrderSelect(
    order: WorkOrder,
    similar: SimilarWorkOrder,
    event: Event,
  ) {
    event.stopPropagation();
    const newIsSelected = !similar.IsSelected;

    this.similarWorkOrderService
      .updateSelection(similar.Uid, newIsSelected)
      .subscribe({
        next: (updatedSimilar: SimilarWorkOrder) => {
          const orderIndex = this.workOrders.findIndex(
            (wo) => wo.SAP_WorkOrderId === order.SAP_WorkOrderId,
          );
          if (orderIndex !== -1) {
            const similarIndex = this.workOrders[
              orderIndex
            ].SimilarWorkOrders.findIndex((s) => s.Uid === updatedSimilar.Uid);
            if (similarIndex !== -1) {
              this.workOrders[orderIndex].SimilarWorkOrders[
                similarIndex
              ].IsSelected = updatedSimilar.IsSelected;
              this.workOrders[orderIndex].SimilarWorkOrders[
                similarIndex
              ].IsSelectedChangedDatetime =
                updatedSimilar.IsSelectedChangedDatetime;
              this.workOrders[orderIndex].SimilarWorkOrders[
                similarIndex
              ].IsSelectedChangedByUser =
                updatedSimilar.IsSelectedChangedByUser;
            }
          }
          this.messageService.add({
            key: 'globalToast',
            severity: 'success',
            summary: 'Ordre similaire mis à jour',
            detail: `L'ordre similaire a été ${newIsSelected ? 'sélectionné' : 'désélectionné'}`,
            life: 1000,
          });
        },
        error: (error) => {
          console.error('Error updating similar order selection:', error);
          this.messageService.add({
            key: 'globalToast',
            severity: 'error',
            summary: 'Erreur',
            detail: "Erreur lors de la mise à jour de l'ordre similaire",
            life: 3000,
          });
        },
      });
  }

  onCategorySelect(order: WorkOrder, category: any, event: Event) {
    event.stopPropagation();
    const newIsSelected = !category.IsSelected;

    this.workOrderSuggestedCategoryService
      .updateWorkOrderSuggestedCategorySelection(category.Uid, newIsSelected)
      .subscribe({
        next: (updatedCategory: any) => {
          const orderIndex = this.workOrders.findIndex(
            (wo) => wo.SAP_WorkOrderId === order.SAP_WorkOrderId,
          );
          if (orderIndex !== -1) {
            const categoryIndex = this.workOrders[
              orderIndex
            ].SuggestedCategories.findIndex(
              (c) => c.Uid === updatedCategory.Uid,
            );
            if (categoryIndex !== -1) {
              this.workOrders[orderIndex].SuggestedCategories[
                categoryIndex
              ].IsSelected = updatedCategory.IsSelected;
              this.workOrders[orderIndex].SuggestedCategories[
                categoryIndex
              ].IsSelectedChangedDatetime =
                updatedCategory.IsSelectedChangedDatetime;
              this.workOrders[orderIndex].SuggestedCategories[
                categoryIndex
              ].IsSelectedChangedByUser =
                updatedCategory.IsSelectedChangedByUser;
            }
          }
          this.messageService.add({
            key: 'globalToast',
            severity: 'success',
            summary: 'Catégorie mise à jour',
            detail: `La catégorie a été ${newIsSelected ? 'sélectionnée' : 'retirée'}`,
            life: 1000,
          });
        },
        error: (error) => {
          console.error('Error updating category selection:', error);
          this.messageService.add({
            key: 'globalToast',
            severity: 'error',
            summary: 'Erreur',
            detail: 'Erreur lors de la mise à jour de la catégorie',
            life: 3000,
          });
        },
      });
  }

  exportPriorities() {
    this.exportLoading = true;
    this.workOrderService
      .getExportWorkOrdersPrioritiesToChange(this.currentFilters)
      .subscribe({
        next: (presignedUrl) => {
          const link = document.createElement('a');
          link.href = presignedUrl;
          link.download = '';
          link.click();
          this.exportLoading = false;
          this.messageService.add({
            key: 'globalToast',
            severity: 'success',
            summary: 'Export réussi',
            detail: 'Le fichier est en cours de téléchargement',
            life: 2000,
          });
        },
        error: (error) => {
          console.error('Error exporting priorities:', error);
          this.exportLoading = false;
          this.messageService.add({
            key: 'globalToast',
            severity: 'error',
            summary: 'Erreur',
            detail: "Erreur lors de l'export des priorités",
            life: 3000,
          });
        },
      });
  }

  exportDuplicates() {
    this.exportDuplicatesLoading = true;
    this.workOrderService
      .getExportWorkOrdersWithDuplicates(this.currentFilters)
      .subscribe({
        next: (presignedUrl) => {
          const link = document.createElement('a');
          link.href = presignedUrl;
          link.download = '';
          link.click();
          this.exportDuplicatesLoading = false;
          this.messageService.add({
            key: 'globalToast',
            severity: 'success',
            summary: 'Export réussi',
            detail: 'Le fichier est en cours de téléchargement',
            life: 2000,
          });
        },
        error: (error) => {
          console.error('Error exporting duplicates:', error);
          this.exportDuplicatesLoading = false;
          this.messageService.add({
            key: 'globalToast',
            severity: 'error',
            summary: 'Erreur',
            detail: "Erreur lors de l'export des doublons",
            life: 3000,
          });
        },
      });
  }

  toggleRightPanel() {
    this.rightPanelOpen = !this.rightPanelOpen;
  }

  onRowSelect(order: WorkOrder) {
    this.selectedOrder = order;
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardNavigation(event: KeyboardEvent) {
    if (!this.selectedOrder || !this.rightPanelOpen) return;

    const currentIndex = this.workOrders.findIndex(
      (wo) => wo.SAP_WorkOrderId === this.selectedOrder?.SAP_WorkOrderId,
    );

    if (
      event.key === 'ArrowDown' &&
      currentIndex < this.workOrders.length - 1
    ) {
      event.preventDefault();
      this.selectedOrder = this.workOrders[currentIndex + 1];
    } else if (event.key === 'ArrowUp' && currentIndex > 0) {
      event.preventDefault();
      this.selectedOrder = this.workOrders[currentIndex - 1];
    }
  }

  private getFilterValue(filters: any, field: string): any {
    const f = filters[field];
    if (!f) return null;
    return Array.isArray(f) ? (f[0]?.value ?? null) : (f.value ?? null);
  }

  private serializeDateFilter(filter: any): string | null {
    if (!filter || this.isInvalidDateRange(filter)) return null;
    const val = Array.isArray(filter) ? filter[0]?.value : filter.value;
    if (!val) return null;
    const dates = Array.isArray(val) ? val : [val];
    return dates
      .filter(Boolean)
      .map((d: Date) => (d instanceof Date ? d.toISOString() : d))
      .join(',');
  }

  private applyFilter(
    filterField: string,
    filterValue: any,
    componentStateUpdates: Partial<{
      selectedPriority: string | null;
      selectedSimilarOrderFilter: string | null;
      selectedWorkOrderTypeFilter: string | null;
    }> = {},
    matchMode: string = 'equals',
  ) {
    this.createdOnDate = null;
    this.originalDueDate = null;
    this.selectedPriority = null;
    this.selectedSimilarOrderFilter = null;
    this.selectedWorkOrderTypeFilter = null;
    this.selectedUserStatus = null;

    Object.assign(this, componentStateUpdates);

    if (this.workOrderTable) {
      this.workOrderTable.first = 0;
      this.workOrderTable.filters = {
        ...cloneDeep(this.INITIAL_TABLE_STATE.filters),
        [WORK_ORDER_FILTER_FIELDS.PLANT]: {
          value: this.selectedPlant,
          matchMode: 'contains',
        },
        [WORK_ORDER_FILTER_FIELDS.MAIN_WORK_CENTER]: {
          value: this.selectedMainWorkCenters,
          matchMode: 'in',
        },
        [filterField]: { value: filterValue, matchMode },
      };
      this.workOrderTable._filter();
    }
  }

  filterBySimilarOrders() {
    this.applyFilter(WORK_ORDER_FILTER_FIELDS.DUPLICATES, 'WITH_DUPLICATES', {
      selectedSimilarOrderFilter: 'WITH_DUPLICATES',
    });
  }

  filterByPM03Category() {
    this.applyFilter(WORK_ORDER_FILTER_FIELDS.WORK_ORDER_TYPE, 'PM03', {
      selectedWorkOrderTypeFilter: 'PM03',
    });
  }

  filterByPriorityToReview() {
    this.applyFilter(WORK_ORDER_FILTER_FIELDS.PRIORITY, 'PRIORITY_TO_REVIEW', {
      selectedPriority: 'PRIORITY_TO_REVIEW',
    });
  }
}
